import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MetaTags from '../components/MetaTags';
import QuizCard from '../components/QuizCard';
import QuizResult from '../components/QuizResult';
import '../styles/QuizPage.css';

// Icônes SVG inline
const IconHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M19 9l-5 5-4-4-3 3" />
  </svg>
);

const IconCardinal = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const IconRitual = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
    <path d="M2 17l10 5 10-5"></path>
    <path d="M2 12l10 5 10-5"></path>
  </svg>
);

const IconGlobe = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const IconKeys = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="4.5"></circle>
    <path d="M10.7 12.3 21 2"></path>
    <path d="m17 5 3 3"></path>
    <path d="m14 8 3 3"></path>
  </svg>
);

// Nombre de questions tirées par partie et temps accordé à chacune.
const QUESTIONS_PAR_QUIZ = 5;
const SECONDES_PAR_QUESTION = 30;

// Le back-office stocke une clé d'icône ; on la résout ici. Une clé inconnue
// retombe sur l'icône générique plutôt que de ne rien afficher.
const ICONES = {
  keys: <IconKeys />,
  history: <IconHistory />,
  cardinal: <IconCardinal />,
  ritual: <IconRitual />,
  globe: <IconGlobe />
};
const iconePour = (cle) => ICONES[cle] || <IconHistory />;


const QuizPage = () => {
  const navigate = useNavigate();
  
  // États pour gérer le quiz
  const [quizCategory, setQuizCategory] = useState(null);
  const [difficulty, setDifficulty] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timer, setTimer] = useState(null);

  // Quiz réellement proposés, tels que déclarés dans le back-office.
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);

  useEffect(() => {
    let annule = false;
    axios.get('/api/quiz/themes')
      .then(res => {
        if (annule) return;
        setThemes(Array.isArray(res.data) ? res.data : []);
        setThemesLoading(false);
      })
      .catch(err => {
        if (annule) return;
        console.error('Erreur lors du chargement des quiz:', err);
        setThemes([]);
        setThemesLoading(false);
      });
    return () => { annule = true; };
  }, []);

  // Une partie se termine à deux endroits (dernière réponse, ou temps écoulé
  // sur la dernière question). On l'enregistre ici plutôt qu'aux deux : l'effet
  // voit le score une fois committé, et le garde évite un double envoi.
  const partieEnregistree = useRef(false);

  useEffect(() => {
    if (!quizComplete || questions.length === 0 || partieEnregistree.current) return;
    partieEnregistree.current = true;

    axios.post('/api/quiz/attempt', {
      category: quizCategory,
      difficulty: difficulty || 'tous',
      score,
      totalQuestions: questions.length
    }).catch(err => {
      // Sans conséquence pour le joueur : la partie reste jouée et affichée.
      console.error('Partie non comptabilisée:', err);
    });
  }, [quizComplete, questions.length, quizCategory, difficulty, score]);

  // Si l'API de questions est prête, cette fonction récupère les questions
  // Récupère les questions depuis l'API. Le back-office (/admin/quiz) est la
  // seule source : la catégorie sélectionnée devient le filtre, ce qui permet
  // d'exploiter plusieurs quiz distincts (conclave, Léon XIV…) sans code dédié.
  const fetchQuestions = async (category, difficulty) => {
    try {
      setLoading(true);
      setError(null);

      const params = { category, count: QUESTIONS_PAR_QUIZ };
      if (difficulty && difficulty !== 'tous') {
        params.difficulty = difficulty;
      }

      const res = await axios.get('/api/quiz/random', { params });

      // L'API renvoie des documents Mongo (_id) ; QuizResult attend un champ id.
      const fetched = (Array.isArray(res.data) ? res.data : []).map(q => ({
        ...q,
        id: q._id || q.id
      }));

      setQuestions(fetched);
      setCurrentQuestionIndex(0);
      setUserAnswers([]);
      setScore(0);
      setQuizComplete(false);
      partieEnregistree.current = false;

      if (fetched.length === 0) {
        setError(
          difficulty && difficulty !== 'tous'
            ? "Aucune question disponible pour ce thème à ce niveau de difficulté. Essayez « Tous niveaux »."
            : "Ce quiz n'a pas encore de questions. Revenez bientôt."
        );
      } else {
        setTimeLeft(SECONDES_PAR_QUESTION);
      }

      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des questions:', err);
      setError('Le quiz n\'a pas pu être chargé. Veuillez réessayer.');
      setQuestions([]);
      setLoading(false);
    }
  };

  // Démarrer le quiz avec la catégorie et la difficulté sélectionnées
  const startQuiz = (category, difficulty) => {
    setQuizCategory(category);
    setDifficulty(difficulty);
    fetchQuestions(category, difficulty);
  };

  // Enregistrer la réponse de l'utilisateur et passer à la question suivante
  const handleAnswer = (answerIndex) => {
    // Enregistrer la réponse
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = answerIndex === currentQuestion.correctAnswer;
    
    const newUserAnswers = [...userAnswers, {
      questionId: currentQuestion.id,
      userAnswer: answerIndex,
      correctAnswer: currentQuestion.correctAnswer,
      isCorrect
    }];
    
    setUserAnswers(newUserAnswers);
    
    // Mettre à jour le score
    if (isCorrect) {
      setScore(score + 1);
    }
    
    // Passer à la question suivante ou terminer le quiz
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Réinitialiser le timer pour la nouvelle question
      setTimeLeft(SECONDES_PAR_QUESTION);
    } else {
      // Quiz terminé
      setQuizComplete(true);
      // Arrêter le timer
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
    }
  };

  // Réinitialiser et recommencer le quiz
  const restartQuiz = () => {
    setQuizCategory(null);
    setDifficulty(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setScore(0);
    setQuizComplete(false);
    setTimeLeft(null);
    partieEnregistree.current = false;
    
    // Arrêter le timer
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
  };

  // Gérer l'écoulement du temps pour chaque question
  useEffect(() => {
    // Si nous sommes en cours de quiz et que le temps est défini
    if (quizCategory && !quizComplete && timeLeft !== null) {
      // Créer un interval pour décrémenter le temps
      const newTimer = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            // Temps écoulé, passer automatiquement à la question suivante
            clearInterval(newTimer);
            
            // Enregistrer la réponse comme incorrecte (timeout)
            const currentQuestion = questions[currentQuestionIndex];
            const newUserAnswers = [...userAnswers, {
              questionId: currentQuestion.id,
              userAnswer: -1, // -1 pour indiquer un timeout
              correctAnswer: currentQuestion.correctAnswer,
              isCorrect: false
            }];
            
            setUserAnswers(newUserAnswers);
            
            // Passer à la question suivante ou terminer le quiz
            if (currentQuestionIndex < questions.length - 1) {
              setCurrentQuestionIndex(prevIndex => prevIndex + 1);
              // Réinitialiser le timer pour la nouvelle question
              return 30;
            } else {
              // Quiz terminé
              setQuizComplete(true);
              return 0;
            }
          }
          return prevTime - 1;
        });
      }, 1000);
      
      setTimer(newTimer);
      
      // Nettoyer l'intervalle en cas de démontage du composant
      return () => clearInterval(newTimer);
    }
  }, [quizCategory, quizComplete, currentQuestionIndex, questions]);

  // Nettoyage lors du démontage du composant
  useEffect(() => {
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timer]);

  // Les quiz proviennent du back-office : la liste en dur mentait — elle
  // proposait cinq thèmes quelle que soit la réalité de la base.

  // Niveaux de difficulté disponibles
  const difficultyLevels = [
    { id: 'débutant', name: 'Débutant', icon: '🔰', description: 'Questions basiques pour ceux qui découvrent le sujet.' },
    { id: 'intermédiaire', name: 'Intermédiaire', icon: '🏆', description: 'Pour ceux qui ont déjà quelques connaissances sur le Vatican.' },
    { id: 'expert', name: 'Expert', icon: '👑', description: 'Questions difficiles pour les vrais connaisseurs.' },
    { id: 'tous', name: 'Tous niveaux', icon: '🔄', description: 'Mélange de questions de toutes les difficultés.' }
  ];

  return (
    <div className="quiz-page">
      <MetaTags 
        title="Quiz du Conclave" 
        description="Testez vos connaissances sur le Conclave, le Collège des Cardinaux et les traditions du Vatican avec notre quiz interactif."
      />
      
      <div className="quiz-header">
        <h1>Quiz du Conclave</h1>
        <p className="subtitle">Testez vos connaissances sur le Vatican et le Collège des Cardinaux</p>
      </div>
      
      {/* Écran de sélection de quiz */}
      {!quizCategory && (
        <div className="quiz-selection">
          {themesLoading ? (
            <p className="quiz-vide">Chargement des quiz…</p>
          ) : themes.length === 0 ? (
            <p className="quiz-vide">
              Aucun quiz n'est proposé pour le moment. Revenez bientôt.
            </p>
          ) : (
            <>
              <h2>Choisissez un quiz</h2>
              <div className="quiz-categories">
                {themes.map(theme => (
                  <div
                    key={theme.slug}
                    className={`quiz-category-card${theme.jouable ? '' : ' indisponible'}`}
                    onClick={() => theme.jouable && setQuizCategory(theme.slug)}
                    role={theme.jouable ? 'button' : undefined}
                    tabIndex={theme.jouable ? 0 : undefined}
                    onKeyDown={e => {
                      if (theme.jouable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setQuizCategory(theme.slug);
                      }
                    }}
                  >
                    <div className="category-icon">{iconePour(theme.icone)}</div>
                    <h3>{theme.nom}</h3>
                    {theme.description && <p>{theme.description}</p>}
                    <span className={`quiz-dispo${theme.jouable ? '' : ' bientot'}`}>
                      {theme.jouable
                        ? `${theme.questionsDisponibles} question${theme.questionsDisponibles > 1 ? 's' : ''}`
                        : 'Bientôt disponible'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Écran de sélection de difficulté */}
      {quizCategory && !difficulty && (
        <div className="quiz-selection">
          <h2>Choisissez un niveau de difficulté</h2>
          <div className="quiz-difficulty-levels">
            {difficultyLevels.map(level => (
              <div 
                key={level.id} 
                className="quiz-difficulty-card"
                onClick={() => startQuiz(quizCategory, level.id)}
              >
                <div className="difficulty-icon">{level.icon}</div>
                <h3>{level.name}</h3>
                <p>{level.description}</p>
              </div>
            ))}
          </div>
          <button className="back-button" onClick={() => setQuizCategory(null)}>
            ← Retour aux catégories
          </button>
        </div>
      )}
      
      {/* Écran de chargement */}
      {loading && (
        <div className="quiz-loading">
          <div className="loader"></div>
          <p>Chargement du quiz...</p>
        </div>
      )}
      
      {/* Affichage des erreurs */}
      {error && (
        <div className="quiz-error">
          <p>{error}</p>
          <button className="restart-button" onClick={restartQuiz}>
            Réessayer
          </button>
        </div>
      )}
      
      {/* Affichage des questions */}
      {!loading && !error && difficulty && !quizComplete && questions.length > 0 && (
        <div className="quiz-question-container">
          <div className="quiz-progress">
            <div className="quiz-progress-text">
              Question {currentQuestionIndex + 1} sur {questions.length}
            </div>
            <div className="quiz-progress-bar">
              <div 
                className="quiz-progress-fill" 
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Timer */}
          <div className="quiz-timer">
            <div className="timer-bar">
              <div 
                className={`timer-fill ${timeLeft <= 5 ? 'timer-warning' : ''}`}
                style={{ width: `${(timeLeft / SECONDES_PAR_QUESTION) * 100}%` }}
              ></div>
            </div>
            <div className="timer-text">{timeLeft} secondes</div>
          </div>
          
          <QuizCard 
            question={questions[currentQuestionIndex]}
            onAnswer={handleAnswer}
            timer={timeLeft}
          />
          
          <button className="quit-button" onClick={restartQuiz}>
            Abandonner le quiz
          </button>
        </div>
      )}
      
      {/* Affichage des résultats */}
      {quizComplete && (
        <QuizResult 
          score={score}
          totalQuestions={questions.length}
          userAnswers={userAnswers}
          questions={questions}
          category={themes.find(t => t.slug === quizCategory)}
          difficulty={difficultyLevels.find(diff => diff.id === difficulty)}
          onRestart={restartQuiz}
        />
      )}
    </div>
  );
};

export default QuizPage;